import { AiChatTurn } from 'src/api/ai'

export type ChatSession = {
  id: string
  title: string
  messages: AiChatTurn[]
  /** Rolling summary of turns that have fallen out of the server's window. */
  summary: string | null
  createdAt: number
  updatedAt: number
}

/** Chats kept on this device. Oldest is dropped once the limit is passed. */
export const MAX_SESSIONS = 5

const STORAGE_PREFIX = 'mas_ai_sessions'
const WIDTH_KEY = 'mas_ai_panel_width'
const CONSENT_PREFIX = 'mas_ai_consent'

/**
 * Chats are stored per account, never under one shared key.
 *
 * localStorage is scoped to the origin, not to the signed-in user, so a single
 * key meant that signing out and back in as someone else surfaced the previous
 * account's conversations - which can contain their game names and revenue.
 */
const keyFor = (userKey: string) => `${STORAGE_PREFIX}::${userKey}`

/**
 * Removes the pre-scoping key plus every other account's chats. Called on
 * logout so nothing of one account's conversation survives into the next
 * session on a shared machine.
 */
export const clearAllStoredSessions = (): void => {
  try {
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && (k === STORAGE_PREFIX || k.startsWith(`${STORAGE_PREFIX}::`))) {
        doomed.push(k)
      }
    }
    doomed.forEach(k => localStorage.removeItem(k))
  } catch {
    // Nothing further to do - the chats simply stay until storage is cleared.
  }
}

export const MIN_WIDTH = 360
export const MAX_WIDTH = 900
export const DEFAULT_WIDTH = 420

export const newSession = (): ChatSession => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: 'New chat',
  messages: [],
  summary: null,
  createdAt: Date.now(),
  updatedAt: Date.now(),
})

/** First user message, trimmed, makes a good enough label for a chat. */
export const titleFor = (session: ChatSession): string => {
  const first = session.messages.find(m => m.role === 'user')?.content?.trim()
  if (!first) return 'New chat'
  return first.length > 38 ? `${first.slice(0, 38)}…` : first
}

/**
 * Storage can be unavailable or throw (private windows, blocked site data), and
 * it is only ever a per-device convenience here, so every access is guarded and
 * the UI works with whatever comes back.
 */
export const loadSessions = (userKey: string): ChatSession[] => {
  if (!userKey) return []
  try {
    // Legacy unscoped key predates per-account storage; drop it on sight.
    localStorage.removeItem(STORAGE_PREFIX)
    const raw = localStorage.getItem(keyFor(userKey))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (s: any) => s && typeof s.id === 'string' && Array.isArray(s.messages)
      )
      .map((s: any) => ({
        id: s.id,
        title: typeof s.title === 'string' ? s.title : 'New chat',
        messages: s.messages.filter(
          (m: any) =>
            (m?.role === 'user' || m?.role === 'assistant') &&
            typeof m?.content === 'string'
        ),
        summary: typeof s.summary === 'string' ? s.summary : null,
        createdAt: Number(s.createdAt) || Date.now(),
        updatedAt: Number(s.updatedAt) || Date.now(),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, MAX_SESSIONS)
  } catch {
    return []
  }
}

export const saveSessions = (
  userKey: string,
  sessions: ChatSession[]
): void => {
  if (!userKey) return
  try {
    const trimmed = [...sessions]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, MAX_SESSIONS)
    localStorage.setItem(keyFor(userKey), JSON.stringify(trimmed))
  } catch {
    // Nothing to do - the chat still works for this page view.
  }
}

export const loadWidth = (): number => {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY))
    if (!raw || Number.isNaN(raw)) return DEFAULT_WIDTH
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, raw))
  } catch {
    return DEFAULT_WIDTH
  }
}

export const saveWidth = (width: number): void => {
  try {
    localStorage.setItem(WIDTH_KEY, String(Math.round(width)))
  } catch {
    // Width just falls back to the default next time.
  }
}

/**
 * Whether this account has agreed to their conversations being recorded.
 *
 * Scoped per account like the chats themselves, so one developer's agreement
 * never silently covers another person signing in on the same browser.
 */
export const hasConsented = (userKey: string): boolean => {
  if (!userKey) return false
  try {
    return localStorage.getItem(`${CONSENT_PREFIX}::${userKey}`) === 'yes'
  } catch {
    return false
  }
}

export const recordConsent = (userKey: string): void => {
  try {
    localStorage.setItem(`${CONSENT_PREFIX}::${userKey}`, 'yes')
  } catch {
    // Without storage they will simply be asked again next time.
  }
}
